import { Fragment, useMemo, useState } from "react";
import {
  createBrowserRouter,
  Link,
  Navigate,
  Outlet,
  RouterProvider,
  ScrollRestoration,
  useLocation,
} from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import AuthProvider, { useAuth } from "./utils/auth";

import { ReactComponent as Rotate } from "./assets/icons/Rotate.svg";
import { ReactComponent as Menu } from "./assets/icons/Menu.svg";
import { ReactComponent as Close } from "./assets/icons/Close.svg";
import { ReactComponent as DashboardIcon } from "./assets/icons/Dashboard.svg";
import { ReactComponent as MyAccountIcon } from "./assets/icons/MyAccount.svg";
import { ReactComponent as SupportIcon } from "./assets/icons/Support.svg";
import { ReactComponent as LogoutIcon } from "./assets/icons/Logout.svg";

import Login from "./screens/Login";
import TermsOfUse from "./screens/TermsOfUse";
import PrivacyPolicy from "./screens/PrivacyPolicy";
import BasicDetails from "./screens/BasicDetails";
import MyAccount from "./screens/MyAccount";
import Support from "./screens/Support";
import Dashboard from "./screens/Dashboard";
import DigitalClinic from "./screens/DigitalClinic";
import FitnessPackage from "./screens/FitnessPackage";
import MentalWellbeing from "./screens/MentalWellbeing";
import YogaMeditation from "./screens/YogaMeditation";
import VisualizationTranquility from "./screens/VisualizationTranquility";
import RelaxationMusic from "./screens/RelaxationMusic";
import MindfulMoments from "./screens/MindfulMoments";
import WellbeingContentDetails from "./screens/WellbeingContentDetails";
import ContentTabs from "./screens/ContentTabs";
import ContentDetails from "./screens/ContentDetails";

const RotateWarn = () => (
  <div className="fixed top-0 bottom-0 left-0 right-0 z-50 h-screen w-screen hidden lg:flex flex-col items-center justify-center bg-primary text-white">
    <Rotate className="shrink-0 h-32 w-36" />
    <p className="mt-6 w-72 text-lg text-center">
      Please rotate your phone to access the application.
    </p>
  </div>
);

const Header = () => {
  const { pathname } = useLocation();
  const {
    logout,
    userData: { fname },
  } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const greetingsText = useMemo(() => {
    const hourNow = new Date().getHours();
    return hourNow < 12
      ? "Good Morning"
      : hourNow < 17
      ? "Good Afternoon"
      : "Good Evening";
  }, []);

  return (
    <main className="min-h-screen relative bg-primary text-secondary">
      <Transition show={isOpen} as={Fragment}>
        <Dialog as={Fragment} onClose={() => setIsOpen(false)}>
          <Transition.Child
            className="fixed top-0 bottom-0 left-0 right-0 bg-black/70"
            enter="duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="duration-300 delay-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Transition.Child
              as={Fragment}
              enter="delay-150 duration-200 transition-transform transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="duration-200 transition-transform transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel
                as="aside"
                className="min-h-full w-64 flex flex-col items-stretch bg-primary text-secondary"
              >
                <div className="flex items-start justify-between p-6">
                  <div className="w-40">
                    <Dialog.Title as="h3" className="text-sm">
                      {greetingsText}
                    </Dialog.Title>
                    <Dialog.Title
                      as="h2"
                      className="text-lg font-medium truncate"
                    >
                      {fname}
                    </Dialog.Title>
                  </div>
                  <button
                    className="shrink-0 bg-ternary p-1.5 rounded-full"
                    type="button"
                    onClick={() => setIsOpen(false)}
                  >
                    <Close className="h-5 w-5 text-white" />
                  </button>
                </div>
                <nav className="mt-4">
                  <ul className="space-y-4">
                    {[
                      {
                        to: "/",
                        label: "Dashboard",
                        icon: <DashboardIcon className="h-5 w-5" />,
                      },
                      {
                        to: "/my-account",
                        label: "My Account",
                        icon: <MyAccountIcon className="h-5 w-5" />,
                      },
                      {
                        to: "/support",
                        label: "Support",
                        icon: <SupportIcon className="h-5 w-5" />,
                      },
                    ].map((linkObj) => (
                      <li key={linkObj.to} className="pr-6">
                        <Link
                          to={linkObj.to}
                          className={`flex items-center space-x-4 py-2 px-6 rounded-r-full${
                            pathname === linkObj.to
                              ? " bg-secondary text-primary"
                              : ""
                          }`}
                          onClick={() => setIsOpen(false)}
                        >
                          {linkObj.icon}
                          <span className="text-xs font-medium">
                            {linkObj.label}
                          </span>
                        </Link>
                      </li>
                    ))}
                    <li className="pr-6">
                      <button
                        type="button"
                        className="w-full flex items-center space-x-4 py-2 px-6 rounded-r-full outline-none text-error"
                        onClick={() => {
                          setIsOpen(false);
                          logout();
                        }}
                      >
                        <LogoutIcon className="h-5 w-5" />
                        <span className="text-xs font-medium">Logout</span>
                      </button>
                    </li>
                  </ul>
                </nav>
              </Dialog.Panel>
            </Transition.Child>
          </Transition.Child>
        </Dialog>
      </Transition>
      <header className="flex items-center justify-between p-6">
        <div className="w-40">
          <h3 className="text-sm">{greetingsText}</h3>
          <h2 className="text-lg font-medium truncate">
            {fname?.length > 0 ? (
              fname
            ) : (
              <Navigate to="/basic-details" replace />
            )}
          </h2>
        </div>
        <button
          className="shrink-0 h-6 w-6"
          type="button"
          onClick={() => setIsOpen(true)}
        >
          <Menu className="w-full h-full" />
        </button>
      </header>
      <Outlet />
    </main>
  );
};

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <AuthProvider>
        <ScrollRestoration /> <Header /> <RotateWarn />
      </AuthProvider>
    ),
    children: [
      { index: true, element: <Dashboard /> },
      { path: "my-account", element: <MyAccount /> },
      { path: "support", element: <Support /> },
      { path: "fitness-package", element: <FitnessPackage /> },
      { path: "mental-wellbeing", element: <MentalWellbeing /> },
      { path: "yoga-meditation", element: <YogaMeditation /> },
      { path: "visualization", element: <VisualizationTranquility /> },
      { path: "relaxation-music", element: <RelaxationMusic /> },
      { path: "mindful-moments", element: <MindfulMoments /> },
      { path: "wellbeing-detail", element: <WellbeingContentDetails /> },
      { path: "content-tabs", element: <ContentTabs /> },
      { path: "content-details", element: <ContentDetails /> },
    ],
  },
  {
    path: "digital-clinic",
    element: (
      <AuthProvider>
        <ScrollRestoration /> <DigitalClinic />
      </AuthProvider>
    ),
  },
  { path: "terms-of-use", element: <TermsOfUse /> },
  { path: "privacy-policy", element: <PrivacyPolicy /> },
  {
    path: "basic-details",
    element: (
      <AuthProvider>
        <ScrollRestoration /> <BasicDetails /> <RotateWarn />
      </AuthProvider>
    ),
  },
  {
    path: "/login",
    element: (
      <AuthProvider>
        <ScrollRestoration /> <Login /> <RotateWarn />
      </AuthProvider>
    ),
  },
]);

const App = () => <RouterProvider router={router} />;

export default App;
